import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Sparkles, Leaf, Droplets, Flower2, Heart, Award, Shield,
  Star, ChevronLeft, ChevronRight, Instagram, Mail, Sun, Moon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ProductCard from '../components/ProductCard';
import SEOHead from '../components/SEOHead';
import EditableImage from '../components/EditableImage';
import { useSiteImages } from '../hooks/useSiteImages';
import api from '../services/api';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────
// HERO SECTION
// ─────────────────────────────────────────────────────────────
const HeroSection = ({ img }) => (
  <section className="relative overflow-hidden bg-brand-ivory grain-overlay" data-testid="hero-section">
    <div className="container mx-auto px-6 lg:px-12 pt-12 pb-20 lg:pt-20 lg:pb-32">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Copy */}
        <div className="relative z-10 animate-fade-in-up">
          <span className="overline mb-6 inline-block">— Botanical Beauty, Made in the UK</span>
          <h1 className="font-display text-[44px] sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight text-brand-espresso mb-6">
            Reveal Your
            <br />
            <span className="italic text-brand-rose-gold">Natural Beauty.</span>
          </h1>
          <p className="text-base sm:text-lg text-brand-taupe max-w-md leading-relaxed mb-10">
            A devoted routine of soft botanicals, gentle gold and quiet luxury — formulated to nurture skin that glows from within.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              asChild
              className="bg-brand-espresso hover:bg-brand-rose-gold text-white rounded-full px-8 h-14 text-[12px] uppercase tracking-[0.25em] font-medium transition-all duration-500 shadow-luxe"
              data-testid="hero-cta-shop"
            >
              <Link to="/shop">
                Shop Now
                <ArrowRight size={16} className="ml-2" strokeWidth={1.5} />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-brand-espresso text-brand-espresso hover:bg-brand-espresso hover:text-white rounded-full px-8 h-14 text-[12px] uppercase tracking-[0.25em] font-medium transition-all duration-500"
              data-testid="hero-cta-collections"
            >
              <Link to="/collections">Explore Collection</Link>
            </Button>
          </div>
          <div className="mt-12 flex items-center gap-8 text-xs text-brand-taupe">
            <span className="flex items-center gap-2"><Leaf size={14} strokeWidth={1.5} className="text-brand-rose-gold" /> 100% Botanical</span>
            <span className="flex items-center gap-2"><Heart size={14} strokeWidth={1.5} className="text-brand-rose-gold" /> Cruelty Free</span>
            <span className="flex items-center gap-2"><Award size={14} strokeWidth={1.5} className="text-brand-rose-gold" /> Made in UK</span>
          </div>
        </div>

        {/* Image */}
        <div className="relative">
          <div className="relative aspect-[4/5] overflow-hidden rounded-luxe-xl shadow-luxe-lg animate-fade-in">
            <EditableImage
              siteKey="home.hero.main"
              src={img('home.hero.main')}
              alt="Luxury botanical skincare routine"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-rose-gold/20 via-transparent to-transparent pointer-events-none" />
          </div>
          <div className="absolute -bottom-6 -left-6 lg:-left-12 glass-warm rounded-luxe-lg p-5 shadow-glass max-w-[220px] animate-float-slow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-blush flex items-center justify-center">
                <Sparkles size={18} strokeWidth={1.5} className="text-brand-rose-gold" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-brand-rose-gold">Loved by</p>
                <p className="font-serif text-brand-espresso">12,000+ women</p>
              </div>
            </div>
          </div>
          <div className="hidden lg:block absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand-blush/40 blur-3xl -z-0" />
        </div>
      </div>
    </div>

    {/* Marquee tagline strip */}
    <div className="border-y border-brand-nude bg-brand-beige/40 overflow-hidden py-4">
      <div className="marquee-track animate-marquee text-brand-rose-gold/80">
        {Array.from({ length: 2 }).map((_, dup) => (
          <div key={dup} className="flex items-center gap-12 pr-12">
            {['Plant-Based', 'Cruelty Free', 'Sensitive-Skin Safe', 'Made UK', 'Vegan Formulas', 'Refillable Range', 'Free Shipping £30+'].map((t, i) => (
              <span key={i} className="flex items-center gap-12 whitespace-nowrap">
                <span className="font-display italic text-2xl tracking-tight">{t}</span>
                <span className="text-brand-rose-gold/60">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// FEATURED PRODUCTS
// ─────────────────────────────────────────────────────────────
const FeaturedProductsSection = ({ products }) => {
  if (!products || products.length === 0) return null;
  return (
  <section className="py-20 lg:py-28 bg-brand-ivory" data-testid="featured-products">
    <div className="container mx-auto px-6 lg:px-12">
      <div className="flex items-end justify-between mb-12 lg:mb-16">
        <div>
          <span className="overline mb-4 inline-block">— Editorʼs Picks</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-brand-espresso leading-tight">
            Featured this <span className="italic text-brand-rose-gold">season</span>
          </h2>
        </div>
        <Link to="/shop" className="hidden md:inline-flex items-center text-[12px] uppercase tracking-[0.25em] text-brand-espresso luxe-link">
          View All <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
        {products.slice(0, 4).map((p, i) => (
          <div key={p.id} className="animate-fade-in-up" style={{ animationDelay: `${i * 80}ms` }}>
            <ProductCard product={p} priority={i < 2} />
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

// ─────────────────────────────────────────────────────────────
// SHOP BY CATEGORY
// ─────────────────────────────────────────────────────────────
const buildCategories = (img) => [
  { name: 'Skincare',   image: img('home.category.skincare'),  to: '/shop/skincare',  tag: 'Best Sellers' },
  { name: 'Haircare',   image: img('home.category.haircare'),  to: '/shop/haircare',  tag: 'New Arrivals' },
  { name: 'Body Care',  image: img('home.category.body') || img('home.category.makeup'), to: '/shop/body', tag: 'Nourish' },
  { name: 'Bundles',    image: img('home.category.fragrance'), to: '/bundles',        tag: 'Save More' },
];

const ShopByCategorySection = ({ img }) => {
  const CATEGORIES = buildCategories(img);
  return (
  <section className="py-20 lg:py-28 bg-brand-beige/40" data-testid="categories-section">
    <div className="container mx-auto px-6 lg:px-12">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <span className="overline mb-4 inline-block">— Explore</span>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-brand-espresso leading-tight mb-4">
          Shop by <span className="italic text-brand-rose-gold">category</span>
        </h2>
        <p className="text-brand-taupe">From cleansing routines to finishing touches — discover the categories crafted with care.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
        {CATEGORIES.map((cat, i) => (
          <Link
            key={cat.name}
            to={cat.to}
            className="group relative aspect-[3/4] overflow-hidden rounded-luxe-lg shadow-luxe hover:shadow-luxe-lg transition-shadow duration-500 animate-fade-in-up"
            style={{ animationDelay: `${i * 80}ms` }}
            data-testid={`category-${cat.name.toLowerCase()}`}
          >
            <div className="absolute inset-0">
              <EditableImage
                siteKey={`home.category.${cat.name.toLowerCase()}`}
                src={cat.image}
                alt={cat.name}
                imgClassName="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-brand-espresso/70 via-brand-espresso/10 to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 p-6 text-white pointer-events-none">
              <span className="text-[10px] uppercase tracking-[0.25em] text-brand-blush mb-2 block">{cat.tag}</span>
              <h3 className="font-display text-2xl lg:text-3xl mb-3">{cat.name}</h3>
              <span className="inline-flex items-center text-[11px] uppercase tracking-[0.2em] luxe-link">
                Discover <ArrowRight size={12} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
  );
};

// ─────────────────────────────────────────────────────────────
// INGREDIENT SPOTLIGHT
// ─────────────────────────────────────────────────────────────
const INGREDIENTS = [
  { name: 'Rose Hip Oil',    icon: Flower2,  blurb: 'Rich in vitamin A & C — visibly evens tone and softens fine lines.' },
  { name: 'Neem Extract',    icon: Leaf,     blurb: 'Ancient apothecary herb that calms breakouts and balances oily skin.' },
  { name: 'Chamomile',       icon: Sun,      blurb: 'Gentle anti-inflammatory ideal for sensitive and reactive complexions.' },
  { name: 'Comfrey Root',    icon: Droplets, blurb: 'Cellular regeneration powerhouse — deeply repairs dryness and damage.' },
  { name: 'Calendula',       icon: Sparkles, blurb: 'Golden marigold petals — soothes redness and restores skin barrier.' },
  { name: 'Vitamin E',       icon: Moon,     blurb: 'Antioxidant shield against free radicals — softens, smooths, protects.' },
];

const IngredientSpotlightSection = () => (
  <section className="py-20 lg:py-28 bg-brand-ivory" data-testid="ingredients-section">
    <div className="container mx-auto px-6 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-32">
          <span className="overline mb-4 inline-block">— Ingredient Spotlight</span>
          <h2 className="font-display text-4xl lg:text-5xl text-brand-espresso leading-tight mb-6">
            Botany meets <span className="italic text-brand-rose-gold">biochemistry.</span>
          </h2>
          <p className="text-brand-taupe leading-relaxed mb-8">
            Every Mirevy formula begins with a single question — what would nature do? Our hero ingredients are grown, harvested and blended to honour the wisdom of generations of botanical apothecaries.
          </p>
          <Button asChild variant="outline" className="border-brand-rose-gold text-brand-rose-gold hover:bg-brand-rose-gold hover:text-white rounded-full px-7 h-12 text-[11px] uppercase tracking-[0.25em]">
            <Link to="/blog">Read the Journal</Link>
          </Button>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
          {INGREDIENTS.map((ing, i) => {
            const Icon = ing.icon;
            return (
              <div
                key={ing.name}
                className="bg-white border border-brand-nude/40 rounded-luxe-lg p-7 shadow-luxe-sm hover:shadow-luxe transition-all duration-500 hover:-translate-y-1 animate-fade-in-up"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`ingredient-${ing.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-12 h-12 rounded-full bg-brand-blush/40 flex items-center justify-center mb-5">
                  <Icon size={22} strokeWidth={1.3} className="text-brand-rose-gold" />
                </div>
                <h3 className="font-serif text-xl text-brand-espresso mb-2">{ing.name}</h3>
                <p className="text-sm text-brand-taupe leading-relaxed">{ing.blurb}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// BEFORE & AFTER GALLERY
// ─────────────────────────────────────────────────────────────
const buildTransformations = (img) => [
  { before: img('home.before_after.1.before'), after: img('home.before_after.1.after'), concern: 'Sensitive Skin', weeks: '6 weeks' },
  { before: img('home.before_after.2.before'), after: img('home.before_after.2.after'), concern: 'Dry & Dehydrated', weeks: '4 weeks' },
  { before: img('home.before_after.3.before'), after: img('home.before_after.3.after'), concern: 'Uneven Tone', weeks: '8 weeks' },
];

const BeforeAfterSection = ({ img }) => {
  const TRANSFORMATIONS = buildTransformations(img);
  return (
  <section className="py-20 lg:py-28 bg-gradient-to-b from-brand-blush/30 via-brand-ivory to-brand-ivory" data-testid="before-after-section">
    <div className="container mx-auto px-6 lg:px-12">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <span className="overline mb-4 inline-block">— Real Results</span>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-brand-espresso leading-tight mb-4">
          Visible <span className="italic text-brand-rose-gold">transformations</span>
        </h2>
        <p className="text-brand-taupe">Unretouched results from our community — skin that breathes, balances and glows.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
        {TRANSFORMATIONS.map((t, i) => (
          <div
            key={i}
            className="group bg-white rounded-luxe-lg shadow-luxe-sm hover:shadow-luxe overflow-hidden transition-all duration-500 animate-fade-in-up"
            style={{ animationDelay: `${i * 100}ms` }}
            data-testid={`transformation-${i}`}
          >
            <div className="grid grid-cols-2">
              <div className="relative aspect-square">
                <EditableImage
                  siteKey={`home.before_after.${i + 1}.before`}
                  src={t.before}
                  alt="Before"
                  className="w-full h-full object-cover"
                />
                <span className="absolute top-3 left-3 z-10 bg-white/90 backdrop-blur text-brand-taupe text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full pointer-events-none">Before</span>
              </div>
              <div className="relative aspect-square">
                <EditableImage
                  siteKey={`home.before_after.${i + 1}.after`}
                  src={t.after}
                  alt="After"
                  className="w-full h-full object-cover"
                />
                <span className="absolute top-3 left-3 z-10 bg-brand-rose-gold text-white text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full pointer-events-none">After</span>
              </div>
            </div>
            <div className="p-6">
              <p className="overline text-brand-rose-gold mb-2">{t.weeks}</p>
              <h3 className="font-serif text-xl text-brand-espresso">{t.concern}</h3>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

// ─────────────────────────────────────────────────────────────
// TESTIMONIALS
// ─────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  { name: 'Eleanor R.', loc: 'London', rating: 5, body: 'Mirevyʼs Neem Cream was the missing piece in my routine. Three weeks in and my breakouts have completely calmed.' },
  { name: 'Margaux D.', loc: 'Paris',  rating: 5, body: 'It feels like a love letter to my skin. The Rosehip Oil glides like silk and the packaging is worthy of being displayed.' },
  { name: 'Aisha K.',   loc: 'Manchester', rating: 5, body: 'I have eczema-prone skin and these are the first products in years that donʼt make me react. Truly transformative.' },
];

const TestimonialsSection = () => (
  <section className="py-20 lg:py-28 bg-brand-espresso text-brand-ivory relative overflow-hidden" data-testid="testimonials-section">
    <div className="absolute inset-0 opacity-20">
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-rose-gold rounded-full blur-[140px]" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-blush rounded-full blur-[140px]" />
    </div>
    <div className="container mx-auto px-6 lg:px-12 relative z-10">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <span className="overline mb-4 inline-block !text-brand-blush">— Loved By Our Community</span>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-brand-ivory leading-tight">
          Whispered <span className="italic text-brand-blush">recommendations</span>
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
        {TESTIMONIALS.map((t, i) => (
          <div
            key={i}
            className="glass rounded-luxe-lg p-8 lg:p-10 animate-fade-in-up"
            style={{ animationDelay: `${i * 100}ms` }}
            data-testid={`testimonial-${i}`}
          >
            <div className="flex gap-1 mb-5">
              {[...Array(t.rating)].map((_, k) => (
                <Star key={k} size={16} strokeWidth={1} className="fill-brand-gold text-brand-gold" />
              ))}
            </div>
            <p className="font-display italic text-xl lg:text-2xl text-brand-espresso leading-snug mb-6">
              “{t.body}”
            </p>
            <p className="font-serif text-brand-espresso">{t.name}</p>
            <p className="text-xs text-brand-taupe tracking-[0.18em] uppercase">{t.loc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// BEST SELLERS CAROUSEL
// ─────────────────────────────────────────────────────────────
const BestSellersCarousel = ({ products }) => {
  const trackRef = useRef(null);
  const scroll = (dir) => {
    if (!trackRef.current) return;
    const w = trackRef.current.clientWidth;
    trackRef.current.scrollBy({ left: dir * w * 0.8, behavior: 'smooth' });
  };
  if (!products || products.length === 0) return null;
  return (
    <section className="py-20 lg:py-28 bg-brand-ivory" data-testid="bestsellers-section">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-12 lg:mb-16">
          <div>
            <span className="overline mb-4 inline-block">— Best Sellers</span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-brand-espresso leading-tight">
              Most <span className="italic text-brand-rose-gold">loved</span>
            </h2>
          </div>
          <div className="flex gap-2">
            <button onClick={() => scroll(-1)} className="w-12 h-12 rounded-full border border-brand-nude flex items-center justify-center hover:bg-brand-blush/30 transition-colors" aria-label="Previous" data-testid="bestseller-prev">
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <button onClick={() => scroll(1)} className="w-12 h-12 rounded-full border border-brand-nude flex items-center justify-center hover:bg-brand-blush/30 transition-colors" aria-label="Next" data-testid="bestseller-next">
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div ref={trackRef} className="flex gap-6 lg:gap-8 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-6 px-6 lg:-mx-12 lg:px-12">
          {products.slice(0, 8).map(p => (
            <div key={p.id} className="snap-start shrink-0 w-[260px] sm:w-[300px] lg:w-[320px]">
              <ProductCard product={p} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// INSTAGRAM-STYLE SOCIAL GALLERY
// ─────────────────────────────────────────────────────────────
const SocialGallerySection = ({ img }) => {
  const SOCIAL_IMAGES = [
    img('home.social.1'),
    img('home.social.2'),
    img('home.social.3'),
    img('home.social.4'),
    img('home.social.5'),
    img('home.social.6'),
  ];
  return (
  <section className="py-20 lg:py-28 bg-brand-beige/30" data-testid="social-section">
    <div className="container mx-auto px-6 lg:px-12">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="overline mb-4 inline-block">— @mirevybeauty</span>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-brand-espresso leading-tight mb-4">
          Tag us <span className="italic text-brand-rose-gold">@mirevybeauty</span>
        </h2>
        <p className="text-brand-taupe">Share your routine — features every Friday on our community wall.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
        {SOCIAL_IMAGES.map((src, i) => (
          <a
            key={i}
            href="https://instagram.com/mirevy"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative aspect-square overflow-hidden rounded-luxe shadow-luxe-sm hover:shadow-luxe transition-all duration-500"
            data-testid={`social-${i}`}
          >
            <EditableImage
              siteKey={`home.social.${i + 1}`}
              src={src}
              alt="Mirevy community"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-brand-espresso/0 group-hover:bg-brand-espresso/40 transition-colors duration-500 flex items-center justify-center pointer-events-none">
              <Instagram size={28} strokeWidth={1.3} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
  );
};

// ─────────────────────────────────────────────────────────────
// NEWSLETTER
// ─────────────────────────────────────────────────────────────
const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.subscribeNewsletter(email);
      toast.success('Welcome to Mirevy. Watch your inbox for a 20% welcome code.');
      setEmail('');
    } catch (err) {
      toast.error('Subscription failed', { description: err?.message || 'Please try again' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 lg:py-28 bg-brand-ivory" data-testid="newsletter-section">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-brand-blush/50 to-brand-beige/40 rounded-luxe-xl p-10 md:p-16 lg:p-20 text-center relative overflow-hidden">
          <Mail size={40} strokeWidth={1.2} className="text-brand-rose-gold mx-auto mb-6" />
          <span className="overline mb-4 inline-block">— Join the Routine</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-brand-espresso mb-5 leading-tight">
            20% off your <span className="italic text-brand-rose-gold">first routine</span>
          </h2>
          <p className="text-brand-taupe max-w-md mx-auto mb-8">
            Subscribe for ingredient stories, slow-beauty letters, and your private 20% welcome code.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="rounded-full h-14 px-6 bg-white border-brand-nude focus:border-brand-rose-gold text-brand-espresso placeholder:text-brand-taupe"
              data-testid="newsletter-email"
            />
            <Button
              type="submit"
              disabled={loading}
              className="rounded-full h-14 px-8 bg-brand-espresso text-white hover:bg-brand-rose-gold uppercase tracking-[0.25em] text-[11px] font-medium transition-colors duration-500"
              data-testid="newsletter-submit"
            >
              {loading ? 'Subscribing…' : 'Subscribe'}
            </Button>
          </form>
          <p className="text-xs text-brand-taupe mt-6">No spam. Unsubscribe anytime.</p>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN HOMEPAGE
// ─────────────────────────────────────────────────────────────
const HomePage = () => {
  const [featured, setFeatured] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const siteImages = useSiteImages();
  const img = (key) => siteImages.get(key, '');

  useEffect(() => {
    (async () => {
      try {
        const [f, b] = await Promise.all([
          api.getProducts({ featured: true, per_page: 8 }).catch(() => ({ products: [] })),
          api.getProducts({ bestseller: true, per_page: 12 }).catch(() => ({ products: [] })),
        ]);
        let fp = f.products || [];
        let bp = b.products || [];
        // Fallback: if backend filters return nothing, use generic list
        if (fp.length === 0 || bp.length === 0) {
          const all = await api.getProducts({ per_page: 12 }).catch(() => ({ products: [] }));
          if (fp.length === 0) fp = (all.products || []).slice(0, 4);
          if (bp.length === 0) bp = (all.products || []).slice(0, 8);
        }
        setFeatured(fp);
        setBestsellers(bp);
      } catch (e) {
        console.error('HomePage load error', e);
      }
    })();
  }, []);

  return (
    <main className="bg-brand-ivory" data-testid="home-page">
      <SEOHead
        title="Mirevy | Botanical Luxury Skincare, Made in the UK"
        description="Reveal your natural beauty with Mirevy — made botanical skincare blending rose, neem, calendula and chamomile. Cruelty-free, sensitive-skin safe, made in the UK."
        keywords={['luxury botanical skincare', 'natural beauty uk', 'sensitive skin', 'made skincare', 'cruelty-free', 'mirevy']}
        canonicalUrl="https://mirevy.com/"
      />

      <HeroSection img={img} />
      <FeaturedProductsSection products={featured} />
      <ShopByCategorySection img={img} />
      <IngredientSpotlightSection />
      <BeforeAfterSection img={img} />
      <TestimonialsSection />
      <BestSellersCarousel products={bestsellers} />
      <SocialGallerySection img={img} />
      <NewsletterSection />
    </main>
  );
};

export default HomePage;
